import internalApi from "./internalApi";

/* Facturation v2 (admin-service) : clients facturés, documents à lignes,
   acomptes, avoirs, encaissements, état des missions, exports. Les montants
   sont des entiers en XOF ; le serveur fait foi pour tous les calculs. Les
   refus métier reviennent en texte (messageServeur les restitue). */

export type TypeDocument = "DEVIS" | "CONTRAT" | "FACTURE" | "AVOIR";
export type EtatPaiement = "NON_PAYEE" | "PARTIELLEMENT_PAYEE" | "PAYEE" | "TROP_PERCUE" | "ANNULEE";
export type ModeEncaissement = "VIREMENT" | "WAVE" | "ORANGE_MONEY" | "CHEQUE" | "ESPECES" | "AUTRE";

export interface ClientFacture {
  id?: number;
  raisonSociale?: string; adresse?: string; pays?: string;
  ninea?: string; ncc?: string; rccm?: string; identifiantFiscal?: string;
  numeroTva?: string; motifExonerationTva?: string;
  contactNom?: string; contactEmail?: string; contactTelephone?: string;
  emailPortail?: string;
}
export interface VueClient { client: ClientFacture; complet: boolean; manquants: string[] }

export interface LigneDocument {
  ordre?: number; libelle: string; quantite: number | string; prixUnitaireHt: number;
  tauxTva: number | string; montantHt: number; montantTva: number; montantTtc: number;
}
export interface DocumentEmis {
  id: number; type: TypeDocument; numero: string; pays: string; questId: string; titreMission?: string;
  clientRaisonSociale?: string; montantHt: number; montantTva: number; montantTtc: number; devise: string;
  emisLe: string; emisPar?: string; dateEcheance?: string | null; natureFacture?: string | null;
  documentOrigineId?: number | null; documentOrigineNumero?: string | null; motif?: string | null;
  statutCertification?: string; numeroFiscal?: string | null; versionModele?: number; lignes: LigneDocument[];
  montantAvoirsTtc?: number | null; montantEncaisse?: number | null; resteDuTtc?: number | null;
  etatPaiement?: EtatPaiement | null;
}
export interface Encaissement {
  id: number; documentId: number; factureNumero: string; montant: number; dateValeur: string;
  mode: ModeEncaissement; referenceExterne?: string | null; note?: string | null; tropPercu: boolean;
  annuleEncaissementId?: number | null; motifAnnulation?: string | null; saisiPar?: string; saisiLe?: string;
}
export interface SuiviFacture {
  document: DocumentEmis;
  avoirs?: { id: number; numero: string; montantTtc: number; emisLe: string }[];
  encaissements?: Encaissement[];
  montantAvoirsTtc?: number; montantEncaisse?: number; resteTtc?: number; etatPaiement?: EtatPaiement;
}
export interface FactureDeMission {
  id: number; numero: string; emisLe: string; dateEcheance?: string | null; natureFacture?: string | null;
  montantTtc: number; montantAvoirsTtc: number; montantEncaisse: number; resteDuTtc: number; etatPaiement: EtatPaiement;
}
export interface EtatMission {
  questId: string; budgetGele: boolean;
  engagement: { id: number; numero: string; type: TypeDocument; emisLe: string; montantHt: number; montantTtc: number } | null;
  factures: FactureDeMission[];
  montantContratTtc: number | null; montantFactureTtc: number; resteAFacturerTtc: number; resteDuTtc: number;
  statutPaiement: string | null;
}

/* ── Clients ── */
export async function listerClients(): Promise<VueClient[]> {
  const r = await internalApi.get("/facturation/v2/clients");
  return Array.isArray(r.data) ? r.data : [];
}
export async function creerClient(c: ClientFacture): Promise<VueClient> {
  return (await internalApi.post("/facturation/v2/clients", c)).data;
}
export async function modifierClient(id: number, c: ClientFacture): Promise<VueClient> {
  return (await internalApi.put(`/facturation/v2/clients/${id}`, c)).data;
}
/** Client rattaché à la mission, ou null (404 : aucun). */
export async function clientDeLaMission(questId: string): Promise<VueClient | null> {
  try {
    return (await internalApi.get(`/facturation/v2/missions/${questId}/client`)).data?.client ?? null;
  } catch (e: any) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}
export async function rattacherClient(questId: string, clientId: number) {
  return (await internalApi.put(`/facturation/v2/missions/${questId}/client`, { clientId })).data;
}

/* ── Documents ── */
export async function documentsDeLaMission(questId: string): Promise<DocumentEmis[]> {
  const r = await internalApi.get("/facturation/v2/documents", { params: { questId } });
  return Array.isArray(r.data) ? r.data : [];
}
export type Acompte = { pourcentage: number } | { montantTtc: number };
export async function emettreDocument(corps: {
  questId: string; type: Exclude<TypeDocument, "AVOIR">; lignes?: Partial<LigneDocument>[]; acompte?: Acompte; cle: string;
}): Promise<DocumentEmis> {
  return (await internalApi.post("/facturation/v2/documents", corps)).data;
}
export async function emettreAvoir(factureId: number, corps: { motif: string; lignes?: Partial<LigneDocument>[]; cle: string }): Promise<DocumentEmis> {
  return (await internalApi.post(`/facturation/v2/documents/${factureId}/avoirs`, corps)).data;
}
export async function suiviFacture(id: number): Promise<SuiviFacture> {
  return (await internalApi.get(`/facturation/v2/documents/${id}`)).data;
}
/** PDF produit et archivé par le serveur, téléchargé tel quel. */
export async function telechargerPdf(id: number, numero: string) {
  const r = await internalApi.get(`/facturation/documents/${id}/pdf`, { responseType: "blob" });
  enregistrer(r.data, `${numero}.pdf`);
}

/* ── Encaissements ── */
export async function encaisser(factureId: number, corps: {
  montant: number; dateValeur: string; mode: ModeEncaissement; referenceExterne?: string; note?: string; tropPercu?: boolean; cle: string;
}): Promise<Encaissement> {
  return (await internalApi.post(`/facturation/v2/documents/${factureId}/encaissements`, corps)).data;
}
export async function annulerEncaissement(id: number, motif: string, cle: string): Promise<Encaissement> {
  return (await internalApi.post(`/facturation/v2/encaissements/${id}/annulation`, { motif, cle })).data;
}

/* ── État des missions ── */
export async function etatMission(questId: string): Promise<EtatMission> {
  return (await internalApi.get(`/facturation/v2/missions/${questId}/etat`)).data;
}
export async function etatsMissions(questIds: string[]): Promise<Record<string, EtatMission>> {
  if (questIds.length === 0) return {};
  return (await internalApi.post("/facturation/v2/missions/etats", { questIds })).data ?? {};
}

/* ── Exports comptables : le CSV du serveur, enregistré sans retraitement ── */
export type JournalExport = "ventes" | "encaissements" | "lignes";
export async function telechargerExport(journal: JournalExport, pays: string, du: string, au: string) {
  try {
    const r = await internalApi.get(`/facturation/v2/exports/${journal}`, { params: { pays, du, au }, responseType: "blob" });
    const disposition: string = r.headers?.["content-disposition"] ?? "";
    const nom = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? `${journal}-${pays}-${du}_${au}.csv`;
    enregistrer(r.data, nom);
  } catch (e: any) {
    // Erreur reçue en blob : son texte est le motif du refus.
    const d = e?.response?.data;
    if (d instanceof Blob) e.response.data = await d.text();
    throw e;
  }
}

/** Clé d'idempotence : une par ouverture de formulaire, renvoyée à chaque nouvel essai. */
export function nouvelleCle(prefixe: string): string {
  const alea = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? (crypto as any).randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefixe}-${alea}`;
}

function enregistrer(contenu: Blob, nom: string) {
  const url = URL.createObjectURL(contenu);
  const a = document.createElement("a");
  a.href = url; a.download = nom; a.click();
  URL.revokeObjectURL(url);
}

/* ── Libellés ── */
export const LIBELLE_ETAT: Record<EtatPaiement, string> = {
  NON_PAYEE: "Non payée", PARTIELLEMENT_PAYEE: "Partiellement payée", PAYEE: "Payée",
  TROP_PERCUE: "Trop-perçue", ANNULEE: "Annulée par avoir",
};
export const LIBELLE_NATURE: Record<string, string> = { TOTALITE: "Totalité", ACOMPTE: "Acompte", SOLDE: "Solde" };
export const LIBELLE_TYPE: Record<TypeDocument, string> = { DEVIS: "Devis", CONTRAT: "Contrat", FACTURE: "Facture", AVOIR: "Avoir" };
export const MODES: { valeur: ModeEncaissement; libelle: string }[] = [
  { valeur: "VIREMENT", libelle: "Virement" }, { valeur: "WAVE", libelle: "Wave" },
  { valeur: "ORANGE_MONEY", libelle: "Orange Money" }, { valeur: "CHEQUE", libelle: "Chèque" },
  { valeur: "ESPECES", libelle: "Espèces" }, { valeur: "AUTRE", libelle: "Autre" },
];

/** « 118000 » → « 118 000 XOF ». */
export const xof = (n?: number | null) =>
  n == null ? "—" : `${Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")} XOF`;
/** « 2026-10-12T10:00 » ou « 2026-10-12 » → « 12/10/2026 » (heure de Dakar, sans conversion). */
export const jour = (iso?: string | null) => {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};
